import { create } from 'zustand'
import apiClient from '@/services/apiClient'
import { CACHE_TTL_MS } from '@/config/constants'
import { appConfig } from '@/config/app'
import type { User } from '@/types/user'
import type { ConfigDto, PackageDto, PaymentHistoryItem, UserSubscriptionDto } from '@/types/api'
import { toSubscriptionDtoList } from '@/utils/dtoAdapters'

type Config = ConfigDto
type Payment = PaymentHistoryItem

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

interface AppState {
  // Auth
  user: User | null
  token: string | null
  isAuthenticated: boolean

  isLoading: boolean
  isSubscriptionsLoading: boolean
  isPackagesLoading: boolean
  subscriptionsError: string | null

  miniAppSettings: {
    fullscreenMode?: 'enabled' | 'disabled'
    externalLinks?: {
      telegramChannelUrl?: string
      telegramCommunityUrl?: string
      supportBotUrl?: string
    }
  } | null

  error: string | null

  // Data
  configs: Config[]
  packages: PackageDto[]
  payments: Payment[]
  subscriptions: UserSubscriptionDto[]
  selectedConfig: Config | null

  // Cache Validations
  lastConfigsFetched: number | null
  lastPackagesFetched: number | null
  lastPaymentsFetched: number | null
  lastSubscriptionsFetched: number | null

  // Actions
  setUser: (user: User | null) => void
  setToken: (token: string) => void
  setIsAuthenticated: (value: boolean) => void
  setIsLoading: (value: boolean) => void
  setConfigs: (configs: Config[]) => void
  setPackages: (packages: PackageDto[]) => void
  setPayments: (payments: Payment[]) => void
  setSubscriptions: (subscriptions: UserSubscriptionDto[]) => void
  setSelectedConfig: (config: Config | null) => void
  addPayment: (payment: Payment) => void
  logout: () => void
  fetchConfigs: (force?: boolean) => Promise<void>
  fetchPackages: (force?: boolean) => Promise<void>
  fetchPayments: (force?: boolean) => Promise<void>
  fetchSubscriptions: (force?: boolean) => Promise<void>
  fetchMiniAppSettings: () => Promise<void>
  fetchLegalPage: (type: string) => Promise<{ title: string; text: string }>
  regenerateSubscriptionLink: (subscriptionId: string) => Promise<UserSubscriptionDto | null>
  resetSubscriptionSessions: (subscriptionId: string) => Promise<boolean>
}

// Helper to safely get from localStorage
const getStorageItem = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null

  try {
    const item = localStorage.getItem(key)
    return item ? (JSON.parse(item) as T) : null
  } catch {
    return null
  }
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  user: getStorageItem<User>('vpnUser'),
  token: typeof window !== 'undefined' ? localStorage.getItem('vpnToken') : null,
  isAuthenticated: typeof window !== 'undefined' ? !!localStorage.getItem('vpnToken') : false,
  isLoading: typeof window !== 'undefined',
  isSubscriptionsLoading: false,
  isPackagesLoading: false,
  subscriptionsError: null,
  miniAppSettings: null,
  configs: [],
  packages: [],
  payments: [],
  subscriptions: [],
  selectedConfig: null,
  lastConfigsFetched: null,
  lastPackagesFetched: null,
  lastPaymentsFetched: null,
  lastSubscriptionsFetched: null,
  error: null,

  // Actions
  fetchConfigs: async (force = false): Promise<void> => {
    const { configs, lastConfigsFetched } = get()
    const now = Date.now()
    // Если данные есть и они свежие, и не требуется принудительное обновление
    if (!force && configs.length > 0 && lastConfigsFetched && (now - lastConfigsFetched < CACHE_TTL_MS)) {
      return
    }

    set({ isLoading: true, error: null })
    try {
      const data = await apiClient.getConfigs()
      set({
        configs: data.configs || [],
        isLoading: false,
        lastConfigsFetched: Date.now()
      })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error, 'Failed to fetch configs'), isLoading: false })
    }
  },

  fetchPackages: async (force = false): Promise<void> => {
    const { packages, lastPackagesFetched } = get()
    const now = Date.now()
    if (!force && packages.length > 0 && lastPackagesFetched && (now - lastPackagesFetched < CACHE_TTL_MS)) {
      return
    }

    set({ isLoading: true, isPackagesLoading: true, error: null })
    try {
      const data = await apiClient.getPackages()
      set({
        packages: data.packages || [],
        isLoading: false,
        isPackagesLoading: false,
        lastPackagesFetched: Date.now()
      })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error, 'Failed to fetch packages'), isLoading: false, isPackagesLoading: false })
    }
  },

  fetchPayments: async (force = false): Promise<void> => {
    const { payments, lastPaymentsFetched } = get()
    const now = Date.now()
    if (!force && payments.length > 0 && lastPaymentsFetched && (now - lastPaymentsFetched < CACHE_TTL_MS)) {
      return
    }

    set({ isLoading: true, error: null })
    try {
      const data = await apiClient.getPayments()
      set({
        payments: data.payments || [],
        isLoading: false,
        lastPaymentsFetched: Date.now()
      })
    } catch (error: unknown) {
      set({ error: getErrorMessage(error, 'Failed to fetch payments'), isLoading: false })
    }
  },

  fetchSubscriptions: async (force = false): Promise<void> => {
    const { lastSubscriptionsFetched } = get()
    const now = Date.now()
    if (!force && lastSubscriptionsFetched && (now - lastSubscriptionsFetched < CACHE_TTL_MS)) {
      return
    }

    // Don't set global isLoading for subscriptions to avoid full page skeleton on navigation if other data is there
    // But we set isSubscriptionsLoading for local spinners
    set({ isSubscriptionsLoading: true, subscriptionsError: null })

    try {
      const data = await apiClient.getMySubscriptions()
      const normalizedSubscriptions = toSubscriptionDtoList(data.subscriptions)

      set({
        subscriptions: normalizedSubscriptions,
        isSubscriptionsLoading: false,
        subscriptionsError: null,
        lastSubscriptionsFetched: Date.now()
      })
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, 'Failed to fetch subscriptions')
      console.error('Failed to fetch subscriptions', error)
      set({ isSubscriptionsLoading: false, subscriptionsError: errorMessage })
    }
  },

  fetchMiniAppSettings: async (): Promise<void> => {
    try {
      const settings = await apiClient.getMiniAppSettings()
      set({ miniAppSettings: settings || null })
    } catch {
      set({
        miniAppSettings: {
          fullscreenMode: 'enabled',
          externalLinks: {
            telegramChannelUrl: appConfig.externalLinks.telegramChannelUrl,
            telegramCommunityUrl: appConfig.externalLinks.telegramCommunityUrl,
            supportBotUrl: appConfig.externalLinks.supportBotUrl,
          },
        },
      })
    }
  },

  fetchLegalPage: async (type: string): Promise<{ title: string; text: string }> => {
    try {
      return await apiClient.getLegalPage(type)
    } catch (error) {
      return {
        title: 'Документ',
        text: `<p>${getErrorMessage(error, 'Не удалось загрузить документ.')}</p>`,
      }
    }
  },

  regenerateSubscriptionLink: async (subscriptionId: string): Promise<UserSubscriptionDto | null> => {
    try {
      const response = await apiClient.regenerateSubscriptionLink(subscriptionId)
      const accessKey = response.subscription?.access_key || null

      let updated: UserSubscriptionDto | null = null
      set((state) => {
        const subscriptions = state.subscriptions.map((item) => {
          if (item.id !== subscriptionId) {
            return item
          }

          updated = {
            ...item,
            access_key: accessKey,
          }

          return updated
        })

        return { subscriptions }
      })

      return updated
    } catch {
      return null
    }
  },

  resetSubscriptionSessions: async (subscriptionId: string): Promise<boolean> => {
    try {
      const response = await apiClient.resetSubscriptionSessions(subscriptionId)
      return Boolean(response.success)
    } catch {
      return false
    }
  },

  setUser: (user): void => {
    if (typeof window !== 'undefined') {
      if (user) {
        localStorage.setItem('vpnUser', JSON.stringify(user))
      } else {
        localStorage.removeItem('vpnUser')
      }
    }
    set({ user })
  },
  setToken: (token): void => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('vpnToken', token)
    }
    set({ token })
  },
  setIsAuthenticated: (isAuthenticated): void => set({ isAuthenticated }),
  setIsLoading: (isLoading): void => set({ isLoading }),
  setConfigs: (configs): void => set({ configs }),
  setPackages: (packages): void => set({ packages }),
  setPayments: (payments): void => set({ payments }),
  setSubscriptions: (subscriptions): void => set({ subscriptions }),
  setSelectedConfig: (selectedConfig): void => set({ selectedConfig }),
  addPayment: (payment): void =>
    set((state) => ({
      payments: [payment, ...state.payments],
    })),
  logout: (): void => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('vpnToken')
      localStorage.removeItem('vpnUser')
    }
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isPackagesLoading: false,
      configs: [],
      packages: [],
      payments: [],
      subscriptions: [],
      selectedConfig: null,
      lastConfigsFetched: null,
      lastPackagesFetched: null,
      lastPaymentsFetched: null,
      lastSubscriptionsFetched: null,
    })
  },
}))
